import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  coerceDateTime,
  coerceLocalDate,
  destinationLabel,
  formatJourneyDateRange,
  lifecycleStatusLabel,
  memberRoleLabel,
  parseDestinationSummary,
} from '../my-journeys/myJourneysModel';
import {
  lifecycleActionCapabilities,
  lifecycleProductHint,
  lifecycleProductLabel,
} from './journeyLifecycleModel';

const EDITABLE_ROLES = new Set(['OWNER', 'COORDINATOR']);
const INVITE_LIFECYCLES = new Set(['DRAFT', 'READY']);

export function canInviteJourneyMembers(journey) {
  return EDITABLE_ROLES.has(journey?.requesterRole)
    && INVITE_LIFECYCLES.has(journey?.lifecycleStatus);
}

export function classifyJourneyAccessError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || '');
  if (code === 92001 || key.includes('not.found') || key.includes('not-found')) {
    return 'not-found';
  }
  if (code === 92002 || key.includes('permission.denied') || key.includes('permission-denied')) {
    return 'forbidden';
  }
  if (key === 'travel.journey.invalid.lifecycle' || key.includes('invalid.lifecycle')) {
    return 'invalid-lifecycle';
  }
  if (code === 92003 || key.includes('concurrent.modification')) {
    return 'conflict';
  }
  if (code === 92000 || key.includes('validation')) {
    return 'validation';
  }
  return 'error';
}

export function normalizeJourneyMember(value) {
  return {
    memberId: value?.memberId,
    role: value?.role || '',
    roleLabel: memberRoleLabel(value?.role),
    membershipStatus: value?.membershipStatus || '',
    joinedAt: coerceDateTime(value?.joinedAt),
    leftAt: coerceDateTime(value?.leftAt),
  };
}

export function normalizeJourneyWorkspace(value, viewerMemberId) {
  const members = Array.isArray(value?.members) ? value.members.map(normalizeJourneyMember) : [];
  const days = Array.isArray(value?.days) ? value.days : [];
  const viewerId = viewerMemberId == null ? null : String(viewerMemberId);
  const self = viewerId
    ? members.find((member) => String(member.memberId) === viewerId && member.membershipStatus === 'ACTIVE')
    : null;
  const requesterRole = self?.role || '';
  const startDate = coerceLocalDate(value?.startDate);
  const endDate = coerceLocalDate(value?.endDate);
  const lifecycleStatus = value?.lifecycleStatus || '';
  const base = {
    journeyId: value?.journeyId,
    creatorMemberId: value?.creatorMemberId ?? null,
    ownerMemberId: value?.ownerMemberId ?? null,
    title: value?.title || 'Untitled journey',
    description: value?.description || '',
    destination: destinationLabel(value?.destinationSummaryJson),
    destinationSummaryJson: value?.destinationSummaryJson || null,
    startDate,
    endDate,
    dateRange: formatJourneyDateRange(startDate, endDate),
    timezone: value?.timezone || '',
    baseCurrency: value?.baseCurrency || '',
    lifecycleStatus,
    lifecycleLabel: lifecycleStatusLabel(lifecycleStatus),
    lifecycleProductLabel: lifecycleProductLabel(lifecycleStatus),
    lifecycleHint: lifecycleProductHint(lifecycleStatus),
    coverUrl: resolveMemberMediaUrl(value?.coverFileReference),
    coverFileReference: value?.coverFileReference || null,
    rowVersion: value?.rowVersion ?? null,
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
    members,
    memberCount: members.filter((member) => member.membershipStatus === 'ACTIVE').length,
    dayCount: days.length,
    days: days.map((day) => ({
      dayId: day?.dayId,
      sequence: day?.sequence,
      date: coerceLocalDate(day?.date),
      title: day?.title || '',
    })),
    requesterRole,
    requesterRoleLabel: memberRoleLabel(requesterRole),
    canEditBasic: EDITABLE_ROLES.has(requesterRole),
    canInvite: canInviteJourneyMembers({ requesterRole, lifecycleStatus }),
  };
  return {
    ...base,
    ...lifecycleActionCapabilities(base),
  };
}

export function destinationTextFromSummary(raw) {
  return destinationLabel(raw);
}

export { parseDestinationSummary };
